import { useMemo, useState } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { SiteLink as Link } from '../SiteLink';
import {
  LIVE_SECTIONS,
  TUTORIALS_PATH,
  sectionOfTopic,
  sectionPath,
  topicLabel,
  tutorialPath,
  tutorialsInSection,
  tutorialsInTopic,
} from '../../content';
import type { Tutorial, TutorialSection } from '../../content';

const matches = (t: Tutorial, needle: string) =>
  `${t.title} ${t.description}`.toLowerCase().includes(needle.toLowerCase());

/** One section's line: its label, its size, and the way to its hub. */
function SectionLine({ section, here }: { section: TutorialSection; here: boolean }) {
  return (
    <Link to={sectionPath(section)} className="tu-side-sec" aria-current={here ? 'page' : undefined}>
      {section.label}
      <span>{tutorialsInSection(section).length}</span>
    </Link>
  );
}

/** A tutorial link, carrying its own headings when it is the page being read. */
function ItemLink({ tutorial, current }: { tutorial: Tutorial; current?: Tutorial }) {
  const here = current?.slug === tutorial.slug;
  return (
    <li>
      <Link to={tutorialPath(tutorial.slug)} aria-current={here ? 'page' : undefined}>
        {tutorial.title}
      </Link>
      {here && tutorial.toc.length > 0 && (
        <ol className="tu-side-toc">
          {tutorial.toc.map((h) => (
            <li key={h.id}>
              <a href={`#${h.id}`}>{h.text}</a>
            </li>
          ))}
        </ol>
      )}
    </li>
  );
}

/**
 * The sections, with the one being read opened out into its topics and
 * tutorials and the rest left as a single line to their hub. The search box
 * ignores that and filters every tutorial on the site.
 *
 * Which section is open comes from the page, not from state, so the
 * prerendered HTML is already correct with JavaScript disabled. Below 960px
 * the whole thing collapses into a disclosure above the article.
 */
export function TutorialSidebar({ current, section }: { current?: Tutorial; section?: TutorialSection }) {
  const [query, setQuery] = useState('');
  const needle = query.trim();
  const openId = (section ?? (current && sectionOfTopic(current.topic)))?.id;
  const hits = useMemo(
    () =>
      needle
        ? LIVE_SECTIONS.map((s) => ({
            section: s,
            items: tutorialsInSection(s).filter((t) => matches(t, needle)),
          })).filter((g) => g.items.length > 0)
        : [],
    [needle],
  );

  const body = (
    <>
      <label className="tu-search">
        <MagnifyingGlass size={15} aria-hidden="true" />
        <span className="sr-only">Search tutorials</span>
        <input type="search" placeholder="Search tutorials" value={query} onChange={(e) => setQuery(e.target.value)} />
      </label>
      <Link to={TUTORIALS_PATH} className="tu-side-all" aria-current={!current && !section ? 'page' : undefined}>
        All tutorials
      </Link>
      {needle && hits.length === 0 && <p className="tu-side-empty">No tutorial matches that yet.</p>}
      {needle
        ? hits.map((g) => (
            <div key={g.section.id} className="tu-side-group">
              <SectionLine section={g.section} here={section?.id === g.section.id} />
              <ul>
                {g.items.map((t) => (
                  <ItemLink key={t.slug} tutorial={t} current={current} />
                ))}
              </ul>
            </div>
          ))
        : LIVE_SECTIONS.map((s) => (
            <div key={s.id} className="tu-side-group">
              <SectionLine section={s} here={section?.id === s.id} />
              {s.id === openId &&
                s.topics.map((topic) => {
                  const items = tutorialsInTopic(topic);
                  if (items.length === 0) return null;
                  return (
                    <div key={topic} className="tu-side-topic">
                      {s.topics.length > 1 && <p className="tu-side-h">{topicLabel(topic)}</p>}
                      <ul>
                        {items.map((t) => (
                          <ItemLink key={t.slug} tutorial={t} current={current} />
                        ))}
                      </ul>
                    </div>
                  );
                })}
            </div>
          ))}
    </>
  );

  return (
    <aside className="tu-side" aria-label="Tutorials">
      <div className="tu-side-wide">{body}</div>
      <details className="tu-side-narrow">
        <summary>{current ? 'All tutorials and this page' : 'Browse tutorials'}</summary>
        {body}
      </details>
    </aside>
  );
}
